import { Box, Stack, Typography } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  describeCitation,
  type KnowledgeMessage,
} from '@/features/internal/knowledge'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_TABULAR,
  FONT_WEIGHT,
  scaledFontSize,
} from '@/shared/styles/typography'

import { AnswerBody } from './AnswerBody'

// 出典番号の gutter 幅。`[10]` まで同じ x に収まる固定値で、本文の左端を番号の桁数に
// 依存させない (固定 gutter の縦一列)。
const CITATION_GUTTER_PX = 26

// 実測 4.5〜13.9 秒 (検索 0.3〜0.8 秒 + 生成 3.7〜13.3 秒。実コーパス 23 資料 1,097 断片・
// 出典 3〜13 件)。数秒で戻るので spinner を置かず、回答位置に 1 行だけ置いて待ちを示す。
const PENDING_LABEL = '資料を確認中…'
// back は生成に失敗しても質問を記録する (何を聞かれて何が返せなかったかが品質レビューの
// 材料になるため)。**回答の無い往復は残る**ので、待ちと区別して事実を書く。
// 区別しないと、終わった往復に「資料を確認中…」が永久に出続ける。
//
// 原因を断定しないのは、回答が無い往復に 2 通りあるため — 生成が失敗した場合と、
// 応答が届く前に接続が切れた場合 (ALB の idleTimeout 60 秒)。後者は back 側で生成が
// 完走して回答が保存されていることがあり、「生成できませんでした」は事実と食い違う。
// 状態列は持たないという方針なので (log/188 work-log/14)、書ける事実は記録の形だけ。
const FAILED_LABEL = '回答が残っていません。もう一度質問してください。'

interface Props {
  question: KnowledgeMessage
  answer?: KnowledgeMessage
  // 生成の応答をまだ待っている往復か。記録済みで回答が無いものは失敗として描く。
  isPending: boolean
}

/**
 * 質問 1 件とその回答。
 *
 * 吹き出し・カード地色・話者ラベルは持たない。質問は字の重みで、回答は本文字格で
 * 区別し、区切りは余白だけが担う (引き算の原則)。
 */
export const ExchangeBlock = ({ question, answer, isPending }: Props) => {
  const citationElementId = (n: number) => `citation-${answer?.id}-${n}`

  return (
    <Box sx={{ mb: 5 }}>
      <Typography
        component="p"
        sx={{
          // 読む本文は文字サイズモードに追従する (ADR-0019)
          fontSize: scaledFontSize('body'),
          fontWeight: FONT_WEIGHT.semibold,
          lineHeight: 1.6,
          color: colorTokens.fg.default,
          whiteSpace: 'pre-wrap',
          mb: 1.5,
        }}
      >
        {question.content}
      </Typography>

      {answer ? (
        <>
          <AnswerBody
            content={answer.content}
            citations={answer.citations}
            citationElementId={citationElementId}
          />
          {answer.citations.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: scaledFontSize('tag'),
                  fontWeight: FONT_WEIGHT.semibold,
                  color: colorTokens.fg.muted,
                  mb: 0.75,
                }}
              >
                出典
              </Typography>
              <Stack spacing={0.25}>
                {answer.citations.map((citation) => (
                  <Box
                    key={citation.n}
                    id={citationElementId(citation.n)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `${CITATION_GUTTER_PX}px 1fr`,
                      columnGap: 0.75,
                      alignItems: 'baseline',
                      borderRadius: `${RADIUS.sm}px`,
                      px: 0.5,
                      mx: -0.5,
                      transition: 'background-color 300ms',
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        ...FONT_TABULAR,
                        fontSize: scaledFontSize('tag'),
                        lineHeight: 1.6,
                        color: colorTokens.fg.faint,
                        textAlign: 'right',
                      }}
                    >
                      [{citation.n}]
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: scaledFontSize('tag'),
                        lineHeight: 1.6,
                        color: colorTokens.fg.muted,
                      }}
                    >
                      {describeCitation(citation)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : null}
        </>
      ) : (
        <Typography
          component="p"
          sx={{
            fontSize: scaledFontSize('bodySm'),
            color: colorTokens.fg.muted,
          }}
        >
          {isPending ? PENDING_LABEL : FAILED_LABEL}
        </Typography>
      )}
    </Box>
  )
}
